import { useApiInfiniteQuery, useApiMutation, useApiQuery } from '@hook/common/useApi';
import * as api from '@api/admin/api-function';
import { EAdminQueryKey } from '@constant/admin/query-key';
import { IApiResponse } from '@datatype/common/api-type';
import {
  IAdminClozeData,
  IAdminClozeMetricData,
  IAdminDailyMetricData,
  IAdminExpressionData,
  IAdminExpressionMetricData,
  IAdminOverviewMetricData,
  IAdminSourceData,
  IAdminStudentMetricData,
  IAdminVocabularyBookData,
  ICreateExpressionBatchRequest,
  IModifyExpressionRequest,
  IReadAdminClozesRequest,
  IReadAdminDailyMetricsRequest,
  IReadAdminExpressionsRequest,
  IReadAdminSourcesRequest,
  IReadAdminStudentMetricsRequest,
  IReadAdminVocabularyBooksRequest,
} from '@datatype/admin/api-type';

export const useAdminExpressionsQuery = (request: IReadAdminExpressionsRequest) => {
  return useApiQuery<IApiResponse<IAdminExpressionData[]>, IReadAdminExpressionsRequest>(
    [
      EAdminQueryKey.ADMIN_EXPRESSIONS,
      request.page,
      request.perPage,
      request.languageCode ?? null,
      request.contentType ?? null,
      request.isPublished ?? null,
      request.isChecked ?? null,
    ],
    api.getAdminExpressions,
    request,
  );
};

export const useAdminExpressionQuery = (expressionKey: string | null) => {
  return useApiQuery<IApiResponse<IAdminExpressionData>, string>(
    [EAdminQueryKey.ADMIN_EXPRESSION, expressionKey ?? ''],
    api.getAdminExpression,
    expressionKey ?? '',
    { enabled: !!expressionKey },
  );
};

export const useCreateExpressionBatchMutation = () => {
  return useApiMutation<IApiResponse<void>, ICreateExpressionBatchRequest>(api.createExpressionBatch);
};

export const useModifyExpressionMutation = () => {
  return useApiMutation<IApiResponse<IAdminExpressionData>, IModifyExpressionRequest>(api.modifyExpression);
};

export const useDeleteExpressionMutation = () => {
  return useApiMutation<IApiResponse<void>, string>(api.deleteExpression);
};

export const useAdminSourcesQuery = (request: Omit<IReadAdminSourcesRequest, 'page'>) => {
  return useApiInfiniteQuery<IAdminSourceData, IReadAdminSourcesRequest>(
    [
      EAdminQueryKey.ADMIN_SOURCES,
      request.perPage,
      request.languageCode ?? null,
      request.sortBy ?? null,
      request.sortOrder ?? null,
    ],
    api.getAdminSources,
    request,
  );
};

export const useAdminSourceQuery = (key: string | null) => {
  return useApiQuery<IApiResponse<IAdminSourceData>, string>(
    [EAdminQueryKey.ADMIN_SOURCE, key ?? ''],
    api.getAdminSource,
    key ?? '',
    { enabled: !!key },
  );
};

export const useDeleteAdminSourceMutation = () => {
  return useApiMutation<IApiResponse<void>, string>(api.deleteAdminSource);
};

export const useAdminClozesQuery = (request: Omit<IReadAdminClozesRequest, 'page'>) => {
  return useApiInfiniteQuery<IAdminClozeData, IReadAdminClozesRequest>(
    [
      EAdminQueryKey.ADMIN_CLOZES,
      request.perPage,
      request.clozeType ?? null,
      request.languageCode ?? null,
    ],
    api.getAdminClozes,
    request,
  );
};

export const useAdminStudentMetricsQuery = (request: Omit<IReadAdminStudentMetricsRequest, 'page'>) => {
  return useApiInfiniteQuery<IAdminStudentMetricData, IReadAdminStudentMetricsRequest>(
    [EAdminQueryKey.ADMIN_STUDENT_METRICS, request.date, request.perPage, request.studied ?? null],
    api.getAdminStudentMetrics,
    request,
  );
};

export const useAdminDailyMetricsQuery = (request: Omit<IReadAdminDailyMetricsRequest, 'page'>) => {
  return useApiInfiniteQuery<IAdminDailyMetricData, IReadAdminDailyMetricsRequest>(
    [EAdminQueryKey.ADMIN_DAILY_METRICS, request.perPage],
    api.getAdminDailyMetrics,
    request,
  );
};

export const useAdminOverviewMetricQuery = () => {
  return useApiQuery<IApiResponse<IAdminOverviewMetricData>, void>(
    [EAdminQueryKey.ADMIN_OVERVIEW_METRIC],
    api.getAdminOverviewMetric,
    undefined,
  );
};

export const useAdminExpressionMetricQuery = () => {
  return useApiQuery<IApiResponse<IAdminExpressionMetricData>, void>(
    [EAdminQueryKey.ADMIN_EXPRESSION_METRIC],
    api.getAdminExpressionMetric,
    undefined,
  );
};

export const useAdminClozeMetricQuery = () => {
  return useApiQuery<IApiResponse<IAdminClozeMetricData>, void>(
    [EAdminQueryKey.ADMIN_CLOZE_METRIC],
    api.getAdminClozeMetric,
    undefined,
  );
};

export const useAdminVocabularyBooksQuery = (request: Omit<IReadAdminVocabularyBooksRequest, 'page'>) => {
  return useApiInfiniteQuery<IAdminVocabularyBookData, IReadAdminVocabularyBooksRequest>(
    [
      EAdminQueryKey.ADMIN_VOCABULARY_BOOKS,
      request.perPage,
      request.languageCode ?? null,
      request.category ?? null,
      request.levels ?? null,
      request.published ?? null,
    ],
    api.getAdminVocabularyBooks,
    request,
  );
};

export const useAssembleAdminVocabularyBookMutation = () => {
  return useApiMutation<IApiResponse<void>, void>(api.assembleAdminVocabularyBook);
};
